<template>
  <Carousel
    ref="carouselRef"
    class="md:hidden w-full"
    :opts="{
      align: 'center',
      loop: false,
    }"
    @init-api="onInitApi"
  >
    <CarouselContent>
      <CarouselItem
        v-for="(tab, index) in tabs"
        :key="tab.title"
        :class="getTabSliderClass(index, tabs.length) + ' flex justify-center basis-auto w-3/5'"
      >
        <Button
          class="rounded-full w-full"
          size="lg"
          @click="selectTab(tab.title, index)"
          :variant="activeTab === tab.title ? 'secondary' : 'greyed'"
        >
          {{ tab.title }}
        </Button>
      </CarouselItem>
    </CarouselContent>
    <CarouselDotButtons class="mt-4 lg:hidden" selected-class="border-primary" />
  </Carousel>
  <Tabs v-model="activeTab">
    <TabsList
      class="hidden md:inline-flex items-center justify-start w-full rounded-full h-12 border-grey-light-200 border bg-grey-light-50"
    >
      <TabsTrigger
        v-for="tab in tabs"
        :key="tab.title"
        :value="tab.title"
        class="rounded-full data-[state=active]:bg-brand-secondary text-grey-light-500 data-[state=active]:text-white cursor-pointer"
      >
        {{ tab.title }}
      </TabsTrigger>
    </TabsList>
    <TabsContent v-for="tab in tabs" :key="tab.title" :value="tab.title" class="mt-12 w-full">
      <Accordion type="single" collapsible class="w-full" :default-value="`${tab.title}-0`">
        <AccordionItem v-for="(faq, index) in tab.faqs" :key="index" :value="`${tab.title}-${index}`" class="py-4">
          <AccordionTrigger class="text-grey-light-900 hover:no-underline text-xl cursor-pointer"
            >{{ faq.question }}
            <template #icon>
              <CirclePlus
                :size="20"
                class="icon-closed text-grey-light-400 pointer-events-none shrink-0 transition-opacity duration-200"
              />
              <CircleMinus
                :size="20"
                class="icon-open text-grey-light-400 pointer-events-none shrink-0 transition-opacity duration-200"
              /> </template
          ></AccordionTrigger>
          <AccordionContent class="text-grey-light-600 text-lg">
            <div v-html="faq.answer"></div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </TabsContent>
  </Tabs>
</template>

<script setup lang="ts">
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselDotButtons,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getTabSliderClass } from '@/utils/slider';
import { CircleMinus, CirclePlus } from 'lucide-vue-next';
import { ref } from 'vue';

export interface FaqAccordian {
  question: string;
  answer: string;
}

export interface FaqTab {
  title: string;
  faqs: FaqAccordian[];
}

interface Props {
  tabs: FaqTab[];
}

const props = defineProps<Props>();

const activeTab = ref(props.tabs[0]?.title);
const carouselApi = ref<CarouselApi>();

const onInitApi = (api: CarouselApi) => {
  carouselApi.value = api;
};

const selectTab = (title: string, index: number) => {
  activeTab.value = title;
  carouselApi.value?.scrollTo(index, false);
};
</script>

<style scoped>
[data-state='open'] .icon-closed {
  display: none;
}

[data-state='closed'] .icon-open {
  display: none;
}
</style>
